class FooterAccordion extends HTMLElement {
  constructor() {
    super();
    this.isAnimating = false;
  }

  connectedCallback() {
    if (this.classList.contains('footer__menu--open')) {
      return;
    }
    this.oneAccordionMode = this.dataset.oneAccordion === 'true';
    this.setupAccordion();
    this.handleResize();
    
    // Track previous viewport state
    this.wasDesktop = window.matchMedia('(min-width: 1280px)').matches;
    
    // Listen for window resize with debounce
    this.resizeHandler = this.debounce(() => {
      this.handleResize();
    }, 150);
    
    window.addEventListener('resize', this.resizeHandler);
  }

  disconnectedCallback() {
    // Clean up event listener
    window.removeEventListener('resize', this.resizeHandler);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  setupAccordion() {
    const detailsElements = this.querySelectorAll('details');

    detailsElements.forEach((details, index) => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.footer__menu-list');
      const icon = details.querySelector('.footer__menu-icon');

      if (!summary || !content) return;

      // Assign unique ID if not already present
      if (!content.id) {
        content.id = `footer-menu-${index}`;
      }

      // Set ARIA attributes
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-controls', content.id);
      summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');

      // Set initial height to 0 if closed
      if (!details.hasAttribute('open')) {
        content.style.height = '0';
        content.style.overflow = 'hidden';
      }

      // Prevent default details behavior and handle manually
      summary.addEventListener('click', (e) => {
        const isDesktop = window.matchMedia('(min-width: 1280px)').matches;
        if (isDesktop) return; // Don't animate on desktop

        e.preventDefault();
        
        if (this.isAnimating) return;
        
        const isOpen = details.hasAttribute('open');
        
        if (isOpen) {
          this.closeAccordion(details, content, icon, summary);
        } else {
          if (this.oneAccordionMode) {
            this.closeAllAccordions(details);
          }
          // Then open the clicked one
          this.openAccordion(details, content, icon, summary);
        }
      });
    });
  }

  closeAllAccordions(exceptThis) {
    const allDetails = this.querySelectorAll('details');
    
    allDetails.forEach((details) => {
      // Skip the one we're about to open and any that are already closed
      if (details === exceptThis || !details.hasAttribute('open')) return;
      
      const content = details.querySelector('.footer__menu-list');
      const icon = details.querySelector('.footer__menu-icon');
      const summary = details.querySelector('summary');
      
      // Close immediately without animation for better UX
      details.removeAttribute('open');
      
      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
      }
      
      if (content) {
        content.style.transition = 'none';
        content.style.height = '0';
        content.style.opacity = '0';
        content.style.marginTop = '0';
        content.style.overflow = 'hidden';
        // Re-enable transitions after a frame
        requestAnimationFrame(() => {
          content.style.transition = 'height 0.3s ease, opacity 0.3s ease, margin-top 0.3s ease';
        });
      }
      
      if (icon) {
        icon.style.transform = 'rotate(0deg)';
      }
    });
  }

  openAccordion(details, content, icon, summary) {
    this.isAnimating = true;
    
    // Set aria first
    summary.setAttribute('aria-expanded', 'true');
    
    // Open the details
    details.setAttribute('open', '');
    
    // Get the natural height
    const height = content.scrollHeight;
    
    // Set height to 0 to start animation
    content.style.height = '0';
    content.style.overflow = 'hidden';
    
    // Force reflow
    content.offsetHeight;
    
    // Animate to natural height
    content.style.transition = 'height 0.3s ease, opacity 0.3s ease, margin-top 0.3s ease';
    content.style.height = height + 'px';
    content.style.opacity = '1';
    
    // Rotate icon
    if (icon) {
      icon.style.transform = 'rotate(180deg)';
    }
    
    // After animation, set height to auto for responsiveness
    setTimeout(() => {
      content.style.height = 'auto';
      content.style.overflow = 'visible';
      this.isAnimating = false;
    }, 300);
  }

  closeAccordion(details, content, icon, summary) {
    this.isAnimating = true;
    
    // Set aria first
    summary.setAttribute('aria-expanded', 'false');
    
    // Get current height
    const height = content.scrollHeight;
    content.style.height = height + 'px';
    content.style.overflow = 'hidden';
    
    // Force reflow
    content.offsetHeight;
    
    // Animate to 0
    content.style.transition = 'height 0.3s ease, opacity 0.3s ease, margin-top 0.3s ease';
    content.style.height = '0';
    content.style.opacity = '0';
    content.style.marginTop = '0';
    
    // Rotate icon back
    if (icon) {
      icon.style.transform = 'rotate(0deg)';
    }
    
    // After animation, remove open attribute
    setTimeout(() => {
      details.removeAttribute('open');
      this.isAnimating = false;
    }, 300);
  }

  handleResize() {
    const detailsElements = this.querySelectorAll('details');
    const isDesktop = window.matchMedia('(min-width: 1280px)').matches;

    detailsElements.forEach((details) => {
      const content = details.querySelector('.footer__menu-list');
      const summary = details.querySelector('summary');
      const icon = details.querySelector('.footer__menu-icon');
      
      if (isDesktop) {
        // Open all on desktop and remove inline styles
        details.setAttribute('open', '');
        if (summary) {
          summary.setAttribute('aria-expanded', 'true');
        }
        if (content) {
          content.style.height = 'auto';
          content.style.overflow = 'visible';
          content.style.opacity = '1';
          content.style.marginTop = '';
        }
        if (icon) {
          icon.style.transform = 'rotate(180deg)';
        }
      } else {
        // If transitioning from desktop to mobile, collapse all accordions
        if (this.wasDesktop && !isDesktop) {
          details.removeAttribute('open');
          if (summary) {
            summary.setAttribute('aria-expanded', 'false');
          }
          if (content) {
            content.style.height = '0';
            content.style.overflow = 'hidden';
            content.style.opacity = '0';
            content.style.marginTop = '0';
          }
          if (icon) {
            icon.style.transform = 'rotate(0deg)';
          }
        } else {
          // Just ensure closed items have correct styles
          if (content && !details.hasAttribute('open')) {
            content.style.height = '0';
            content.style.overflow = 'hidden';
            content.style.opacity = '0';
            content.style.marginTop = '0';
          }
        }
      }
    });

    // Update the previous state
    this.wasDesktop = isDesktop;
  }
}

export default FooterAccordion;

